import React, { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Box, Button } from '@mui/material';
import { ArrowBack as ArrowBackIcon, History as HistoryIcon } from '@mui/icons-material';
import FileUploadZone from '../Components/FileUploadZone';
import ImportProgressTracker from '../Components/ImportProgressTracker';
import Fa3BackfillOperationsPanel from '../Components/Fa3BackfillOperationsPanel';
import { useFileUpload } from '../Hooks/useFileUpload';
import { InvoiceImportStatus } from '../Types/invoiceImport.types';
import { ExpenseInvoicesRoutes } from '../../../Router/path';
import { useNotification } from '../../Invoices/Context/NotificationContext';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import type { UserInvoiceCorrections } from '../Types/userInvoiceCorrections';
import { useAuthUser } from '../../Auth/hooks/useAuthHooks';
import { resolveFa3OperationsAccessPolicy } from '../Utils/fa3OperationsAccessPolicy';

/**
 * Main page for invoice import feature
 * Handles file upload → polling → navigation with toast notifications
 */
const InvoiceImportPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authUser = useAuthUser();
  const { showSuccess, showError } = useNotification();
  const { uploadFile, isLoading, validationError, apiError, result, clearState } = useFileUpload();
  const [importJobId, setImportJobId] = useState<number | null>(null);
  const shouldFocusFa3Operations = searchParams.get('tool') === 'fa3-backfill';
  const { canViewOperations: canViewFa3Operations, canExecuteOperations: canExecuteFa3Operations } =
    resolveFa3OperationsAccessPolicy(authUser?.roles);
  const shouldShowFa3Operations = shouldFocusFa3Operations && canViewFa3Operations;

  const handleFileSelect = async (file: File, corrections?: UserInvoiceCorrections) => {
    // Pass corrections to backend (if OCR was used)
    const uploadResult = await uploadFile(file, undefined, corrections);

    // Start tracking if we get an importJobId
    if (uploadResult?.importJobId) {
      setImportJobId(uploadResult.importJobId);

      if (corrections && Object.keys(corrections).length > 0) {
        try {
          sessionStorage.setItem(
            `invoiceImport:ocrCorrections:${uploadResult.importJobId}`,
            JSON.stringify(corrections)
          );
        } catch {}
      }

      showSuccess(`Plik ${file.name} został przesłany. Trwa przetwarzanie...`);
    } else if (!uploadResult) {
      // Upload failed (validation or API error)
      showError(validationError || 'Nie udało się przesłać pliku. Spróbuj ponownie.');
    }
  };

  const handleRetry = () => {
    clearState();
    setImportJobId(null);
  };

  const handleBackToList = () => {
    navigate(ExpenseInvoicesRoutes.root);
  };

  // Stable reference required: ImportProgressTracker's status-change effect depends on this
  // callback. An inline/unmemoized function here is a new reference on every render, and since
  // the Failed/Duplicate branches call showError (which updates NotificationContext state and
  // re-renders this page), that re-render would create a new callback reference, re-triggering
  // the child effect and calling showError again — an infinite render loop that froze every
  // button on the page as soon as a Failed/Duplicate status appeared.
  const handleStatusChange = useCallback(
    (status: InvoiceImportStatus) => {
      // Handle terminal states with toast notifications
      // Note: RequiresReview is handled by ImportProgressTracker with auto-redirect
      if (status === InvoiceImportStatus.Failed) {
        showError('Import faktury nie powiódł się. Sprawdź szczegóły poniżej.');
      } else if (status === InvoiceImportStatus.Duplicate) {
        showError('Ten plik został już wcześniej zaimportowany.');
      }
    },
    [showError]
  );

  // Extract error message from API error
  const apiErrorMessage = apiError
    ? extractApiErrorMessage(apiError, 'Błąd połączenia z serwerem')
    : null;

  const displayError = validationError || apiErrorMessage;

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        {/* Header buttons */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBackToList} variant="text">
            Powrót do listy faktur
          </Button>

          <Button
            startIcon={<HistoryIcon />}
            onClick={() => navigate(ExpenseInvoicesRoutes.importHistory)}
            variant="outlined"
          >
            Historia importu
          </Button>
        </Box>

        {shouldShowFa3Operations ? (
          <Fa3BackfillOperationsPanel
            autoFocus={shouldFocusFa3Operations}
            canExecuteOperations={canExecuteFa3Operations}
          />
        ) : null}

        {/* Show upload zone if no job tracking */}
        {!importJobId && (
          <FileUploadZone
            onFileSelect={handleFileSelect}
            isLoading={isLoading}
            error={displayError || undefined}
          />
        )}

        {/* Show progress tracker when tracking job */}
        {importJobId && (
          <Box sx={{ mt: 3 }}>
            <ImportProgressTracker
              importJobId={importJobId}
              autoRedirect={true}
              redirectDelayMs={2000}
              onStatusChange={handleStatusChange}
            />

            {/* Retry button (shown below tracker for failed/duplicate) */}
            {result &&
              (result.status === InvoiceImportStatus.Failed ||
                result.status === InvoiceImportStatus.Duplicate) && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button variant="outlined" onClick={handleRetry}>
                    Importuj nowy plik
                  </Button>
                </Box>
              )}
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default InvoiceImportPage;
